// useEntityDetailsCapabilities — tier resolver for the per-entity
// Details / Bilagor surface (`EntityDetailsDialog`).
//
// Mirrors `useFinalCapabilities` exactly so future tier-gated reads
// across the codebase look identical. Two state bits matter:
//   - `entityDetailsEnabled` — does the active plan include the
//     entity_details + entity_attachments storage that powers the
//     dialog? Free plan: NO. Advanced+ on paid plans: YES.
//   - `currentTier` + `entityDetailsMinTier` — for upsell-card copy
//     ("Upgrade to Advanced to use Details").
//
// Build-mode is intentionally NOT a separate flag here. Unlike the
// Atlas / Final isolation policy (build-time strip for non-Atlas
// SKUs), Details is always present in the bundle — only the runtime
// dialog body changes for free users.

import { useMemo } from 'react';
import { usePlan } from '@/hooks/usePlan';
import { canUse, type PlanTier } from '@/lib/planTiers';

export interface EntityDetailsCapabilities {
  /** True when the plan grants the Details/Bilagor surface. */
  entityDetailsEnabled: boolean;
  /** Lowest tier that unlocks Details (for upsell-card copy). */
  entityDetailsMinTier: PlanTier;
  /** Current user's plan tier (for the upsell to compare against). */
  currentTier: PlanTier;
}

export function useEntityDetailsCapabilities(): EntityDetailsCapabilities {
  const { tier, limits } = usePlan();
  return useMemo(() => {
    const gate = canUse(limits, 'entity-details');
    return {
      entityDetailsEnabled: limits.entityDetailsEnabled,
      entityDetailsMinTier: (gate.requiredTier ?? 'advanced') as PlanTier,
      currentTier: tier,
    };
  }, [limits, tier]);
}
